import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { Users, Search, LayoutGrid, List, ArrowLeft, ChevronRight, Trash2, Building2, Wrench } from "@/components/icons";
import Portal from "../../components/Portal";
import { toast } from "sonner";

/**
 * Admin-only page: every patient in the platform, grouped by doctor or
 * shown as a flat searchable list.
 */
export default function AdminPatients() {
  const [items, setItems] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("grouped"); // "grouped" | "flat"
  const [params, setParams] = useSearchParams();
  const selectedDoctorId = params.get("doctor") || null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, u, o] = await Promise.all([
          api.get("/patients/admin/all"),
          api.get("/users?role=doctor"),
          api.get("/offices"),
        ]);
        if (!cancelled) {
          setItems(r.data || []);
          setDoctors(u.data || []);
          setOffices(o.data || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const doctorsWithOffice = useMemo(() => {
    const s = new Set();
    for (const o of offices) if (o.doctor_id) s.add(o.doctor_id);
    return s;
  }, [offices]);

  // Pacientes "huérfanos": sin office_id. El backfill sólo puede reparar los
  // cuyo doctor tiene UNA sola oficina, así que contamos ambas cifras.
  const orphanStats = useMemo(() => {
    const officesByDoc = {};
    for (const o of offices) {
      if (!o.doctor_id) continue;
      (officesByDoc[o.doctor_id] ||= []).push(o.id);
    }
    let repairable = 0;
    let total = 0;
    for (const p of items) {
      if (p.office_id) continue;
      total += 1;
      if ((officesByDoc[p.doctor_id] || []).length === 1) repairable += 1;
    }
    return { total, repairable };
  }, [items, offices]);

  const [repairing, setRepairing] = useState(false);
  const runRepair = async () => {
    if (orphanStats.repairable === 0) {
      toast.info("No hay pacientes huérfanos que reparar automáticamente.");
      return;
    }
    if (
      !window.confirm(
        `Se asignará la oficina única a ${orphanStats.repairable} paciente${
          orphanStats.repairable === 1 ? "" : "s"
        } huérfano${orphanStats.repairable === 1 ? "" : "s"}. ¿Continuar?`,
      )
    ) {
      return;
    }
    setRepairing(true);
    try {
      const { data } = await api.post("/patients/admin/backfill-office-id");
      toast.success(
        `Reparados: ${data.total} paciente${data.total === 1 ? "" : "s"}`,
      );
      // Refresca la lista
      const r = await api.get("/patients/admin/all");
      setItems(r.data || []);
    } catch (err) {
      toast.error("No se pudo ejecutar la reparación.");
    } finally {
      setRepairing(false);
    }
  };

  const docById = useMemo(() => {
    const m = {};
    for (const d of doctors) m[d.id] = d;
    return m;
  }, [doctors]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((p) =>
      [p.full_name, p.email, p.phone, p.doctor_name, p.doctor_email]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(needle))
    );
  }, [items, q]);

  const grouped = useMemo(() => {
    const m = new Map();
    // Seed with EVERY doctor — including those with zero patients — so the
    // admin sees them in the list. The filter on `q` (search) is applied
    // separately below.
    for (const d of doctors) {
      m.set(d.id, {
        doctor_id: d.id,
        doctor_name: d.name || "—",
        doctor_email: d.email || "",
        doctor_phone: d.phone || "",
        doctor_address: d.address || "",
        patients: [],
      });
    }
    for (const p of filtered) {
      const key = p.doctor_id || "__orphan__";
      if (!m.has(key)) {
        const doc = p.doctor_id ? docById[p.doctor_id] : null;
        m.set(key, {
          doctor_id: p.doctor_id,
          doctor_name: p.doctor_name || doc?.name || "Sin doctor asignado",
          doctor_email: p.doctor_email || doc?.email || "",
          doctor_phone: doc?.phone || "",
          doctor_address: doc?.address || "",
          patients: [],
        });
      }
      m.get(key).patients.push(p);
    }
    // When a search query is active, drop doctors that match neither the
    // free-text on their own name/email nor any of their patients.
    let groups = Array.from(m.values());
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      groups = groups.filter((g) => {
        const docMatch = [g.doctor_name, g.doctor_email, g.doctor_phone]
          .join(" ")
          .toLowerCase()
          .includes(needle);
        return docMatch || g.patients.length > 0;
      });
    }
    return groups.sort((a, b) =>
      (a.doctor_name || "").localeCompare(b.doctor_name || ""),
    );
  }, [filtered, docById, doctors, q]);

  return (
    <div className="space-y-6" data-testid="admin-patients-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-black">
          Pacientes
        </h1>
        <p className="text-sm text-black/55 mt-1">
          Todos los pacientes registrados en la plataforma.
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40" />
          <input
            data-testid="admin-patients-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por paciente, email o doctor…"
            className="w-full bg-white border border-[#E5E7EB] rounded-2xl pl-11 pr-4 py-2.5 text-sm text-black placeholder-black/40 focus:outline-none focus:border-black/60"
          />
        </div>
        <div className="inline-flex items-center bg-white border border-[#E5E7EB] rounded-full p-1">
          <button
            onClick={() => setView("grouped")}
            data-testid="view-grouped-btn"
            className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 transition ${
              view === "grouped"
                ? "bg-black text-white"
                : "text-black/60 hover:text-black"
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Por doctor
          </button>
          <button
            onClick={() => setView("flat")}
            data-testid="view-flat-btn"
            className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 transition ${
              view === "flat"
                ? "bg-black text-white"
                : "text-black/60 hover:text-black"
            }`}
          >
            <List className="w-3.5 h-3.5" /> Todos juntos
          </button>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {orphanStats.total > 0 && (
            <button
              type="button"
              onClick={runRepair}
              disabled={repairing || orphanStats.repairable === 0}
              data-testid="repair-orphan-patients-btn"
              title={
                orphanStats.repairable === 0
                  ? `Hay ${orphanStats.total} paciente(s) huérfanos, pero sus doctores tienen múltiples oficinas y requieren asignación manual.`
                  : `Asigna la oficina única a ${orphanStats.repairable} paciente(s) huérfano(s).`
              }
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold border transition ${
                orphanStats.repairable === 0
                  ? "bg-white border-[#E5E7EB] text-black/40 cursor-not-allowed"
                  : "bg-[#01BC8E] border-[#01BC8E] text-white hover:bg-[#019976]"
              }`}
            >
              <Wrench className="w-3.5 h-3.5" />
              {repairing
                ? "Reparando…"
                : `Reparar huérfanos (${orphanStats.repairable}/${orphanStats.total})`}
            </button>
          )}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E5E7EB] text-xs font-bold text-black/65">
            <Users className="w-3.5 h-3.5" /> {filtered.length} paciente{filtered.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-black/55 py-10">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-[28px] border border-black/[0.06] p-10 text-center text-black/55">
          No hay pacientes que coincidan.
        </div>
      ) : view === "flat" ? (
        <FlatTable rows={filtered} />
      ) : selectedDoctorId ? (
        <DoctorPatientsView
          doctorId={selectedDoctorId}
          all={items}
          search={q}
          onBack={() => setParams({})}
        />
      ) : (
        <DoctorsGrid groups={grouped} doctorsWithOffice={doctorsWithOffice} onPick={(d) => setParams({ doctor: d.doctor_id || "__orphan__" })} />
      )}
    </div>
  );
}

function DoctorsGrid({ groups, doctorsWithOffice, onPick }) {
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purging, setPurging] = useState(false);

  const confirmPurge = async () => {
    if (!purgeTarget?.doctor_id) return;
    setPurging(true);
    try {
      const { data } = await api.delete(
        `/patients/admin/by-doctor/${purgeTarget.doctor_id}`,
      );
      toast.success(
        `Eliminados ${data.deleted} paciente${data.deleted === 1 ? "" : "s"} de ${purgeTarget.doctor_name}`,
      );
      setPurgeTarget(null);
      // Force a soft reload by triggering navigation/refresh
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setPurging(false);
    }
  };

  if (groups.length === 0)
    return <div className="text-center text-black/55 py-10">Sin doctores</div>;
  return (
    <div className="bg-white rounded-[28px] border border-black/[0.06] p-3 sm:p-4" data-testid="doctors-grid">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-black/45">
            <th className="px-4 py-3">Doctor</th>
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Teléfono</th>
            <th className="px-4 py-3">Dirección</th>
            <th className="px-4 py-3 text-right">Pacientes</th>
            <th className="px-4 py-3 text-right">Acciones</th>
            <th className="px-4 py-3 w-10"></th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const initials = (g.doctor_name || "?")
              .split(" ")
              .map((s) => s[0])
              .filter(Boolean)
              .slice(0, 2)
              .join("")
              .toUpperCase();
            return (
              <tr
                key={g.doctor_id || "orphan"}
                onClick={() => onPick(g)}
                data-testid={`doctor-row-${g.doctor_id || "orphan"}`}
                className="border-t border-black/[0.06] first:border-t-0 hover:bg-black/[0.03] cursor-pointer transition"
              >
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white border border-[#E5E7EB] text-black text-xs font-extrabold flex items-center justify-center flex-shrink-0">
                      {initials}
                    </div>
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="font-bold text-black truncate">{g.doctor_name}</span>
                      {g.doctor_id && !doctorsWithOffice.has(g.doctor_id) && (
                        <span
                          data-testid={`doctor-onboarding-pending-${g.doctor_id}`}
                          title="El doctor aún no ha creado su primera oficina"
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-indigo-700 bg-indigo-100 rounded-full px-1.5 py-0.5 w-fit"
                        >
                          <Building2 className="w-3 h-3" />
                          Onboarding pendiente
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-black/70">
                  {g.doctor_email || "—"}
                </td>
                <td className="px-4 py-4 text-black/70 whitespace-nowrap">
                  {g.doctor_phone ? (
                    <a
                      href={`tel:${g.doctor_phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:text-black hover:underline"
                    >
                      {g.doctor_phone}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-4 py-4 text-black/70 truncate max-w-[260px]" title={g.doctor_address || ""}>
                  {g.doctor_address || "—"}
                </td>
                <td className="px-4 py-4 text-right">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-xs font-bold text-black border border-[#E5E7EB]">
                    <Users className="w-3.5 h-3.5" />
                    {g.patients.length}
                  </span>
                </td>
                <td
                  className="px-4 py-4 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  {g.doctor_id && g.patients.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setPurgeTarget(g)}
                      data-testid={`doctor-purge-${g.doctor_id}`}
                      title="Eliminar todos los pacientes de este doctor"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-bold transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Eliminar todos
                    </button>
                  )}
                </td>
                <td className="px-2 py-4 text-right">
                  <ChevronRight className="w-4 h-4 text-black/40 inline" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {purgeTarget && (
        <PurgeConfirmModal
          group={purgeTarget}
          saving={purging}
          onCancel={() => setPurgeTarget(null)}
          onConfirm={confirmPurge}
        />
      )}
    </div>
  );
}

function PurgeConfirmModal({ group, saving, onCancel, onConfirm }) {
  const expected = String(group.patients.length);
  const [typed, setTyped] = useState("");
  const matches = typed.trim() === expected;
  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onCancel}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-[24px] p-6 w-full max-w-md space-y-4"
          data-testid="purge-confirm-modal"
        >
          <h3 className="text-lg font-extrabold text-rose-700">
            Eliminar todos los pacientes de {group.doctor_name}
          </h3>
          <p className="text-sm text-black/70">
            Esta acción es <span className="font-bold">irreversible</span>. Se
            eliminarán <span className="font-bold">{group.patients.length}</span>{" "}
            paciente{group.patients.length === 1 ? "" : "s"} y todo su
            historial (diagnósticos, citas, recetas, archivos, pagos,
            laboratorio).
          </p>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-black/55">
              Escribe <span className="text-rose-700">{expected}</span> para confirmar
            </label>
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              data-testid="purge-confirm-input"
              className="mt-1 w-full bg-[#F5F5F5] rounded-2xl px-4 py-2.5 text-sm focus:outline-none border border-transparent focus:border-rose-300"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-xl text-black font-bold text-sm hover:bg-black/[0.05]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!matches || saving}
              data-testid="purge-confirm-submit"
              className="px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm shadow disabled:opacity-50"
            >
              {saving ? "Eliminando…" : "Eliminar todo"}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function DoctorPatientsView({ doctorId, all, search, onBack }) {
  const list = useMemo(() => {
    const id = doctorId === "__orphan__" ? null : doctorId;
    const subset = all.filter((p) => (p.doctor_id || null) === id);
    const needle = search.trim().toLowerCase();
    if (!needle) return subset;
    return subset.filter((p) =>
      [p.full_name, p.email, p.phone]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(needle)),
    );
  }, [doctorId, all, search]);

  const docName = list[0]?.doctor_name || "Sin doctor asignado";
  const docEmail = list[0]?.doctor_email || "";

  return (
    <div className="space-y-4" data-testid="doctor-patients-view">
      <button
        type="button"
        onClick={onBack}
        data-testid="doctor-patients-back"
        className="inline-flex items-center gap-1.5 text-xs font-bold text-black/65 hover:text-black"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a doctores
      </button>
      <div className="bg-white rounded-[28px] border border-black/[0.06] p-5 sm:p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h2 className="text-xl font-extrabold text-black">{docName}</h2>
            {docEmail && <p className="text-xs text-black/55">{docEmail}</p>}
          </div>
          <span className="text-xs font-bold text-black/55">
            {list.length} paciente{list.length === 1 ? "" : "s"}
          </span>
        </div>
        {list.length === 0 ? (
          <div className="text-center text-black/55 py-8">Sin pacientes</div>
        ) : (
          <FlatTable rows={list} hideDoctor />
        )}
      </div>
    </div>
  );
}

function FlatTable({ rows, hideDoctor = false }) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-[#E5E7EB]">
      <table className="w-full text-sm">
        <thead className="bg-[#F5F5F5] text-[10px] font-bold uppercase tracking-widest text-black/55">
          <tr>
            <th className="text-left px-4 py-3">Paciente</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Teléfono</th>
            {!hideDoctor && <th className="text-left px-4 py-3">Doctor</th>}
            <th className="text-left px-4 py-3">Sexo</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-[#E5E7EB] hover:bg-black/[0.02] text-black">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="relative w-9 h-9 flex-shrink-0">
                    <div className="absolute inset-0 rounded-full bg-[#DBEAFE] text-[#1B405B] text-xs font-extrabold flex items-center justify-center overflow-hidden">
                      {(p.full_name || "?")
                        .split(" ")
                        .map((s) => s[0])
                        .filter(Boolean)
                        .slice(0, 2)
                        .join("")
                        .toUpperCase()}
                    </div>
                    {p.photo_url && p.photo_url.length > 100 && (
                      <img
                        src={p.photo_url}
                        alt=""
                        className="absolute inset-0 w-full h-full rounded-full object-cover"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    )}
                  </div>
                  <span className="font-bold">{p.full_name}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-black/80">{p.email || "—"}</td>
              <td className="px-4 py-3 text-black/80">{p.phone || "—"}</td>
              {!hideDoctor && (
                <td className="px-4 py-3 text-black/70">
                  {p.doctor_name || (
                    <span className="text-red-700 font-bold">huérfano</span>
                  )}
                </td>
              )}
              <td className="px-4 py-3 text-black/65">{p.sex || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
